import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { 
  CreditCard, ExternalLink, CheckCircle, AlertCircle, 
  Clock, DollarSign, RefreshCw, ArrowRight, Building,
  TrendingUp, ArrowUpRight, ArrowDownRight, History
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../../App";
import { useFeatureGate } from "../../hooks/useFeatureGate";
import FeatureGate from "../../components/FeatureGate";

const PaymentsPage = () => {
  const [connectStatus, setConnectStatus] = useState(null);
  const [earnings, setEarnings] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [onboarding, setOnboarding] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");
  
  // Feature gating
  const { hasFeature } = useFeatureGate();
  const hasPayments = hasFeature("accept_payments");

  useEffect(() => {
    // Only fetch if user has payments feature
    if (hasPayments) {
      fetchData();
    } else {
      setLoading(false);
    }
  }, [hasPayments]);

  const fetchData = async () => {
    try {
      const [statusRes, earningsRes, transactionsRes] = await Promise.all([
        axios.get(`${API}/stripe/connect/status`, { withCredentials: true }),
        axios.get(`${API}/stripe/connect/earnings?period=month`, { withCredentials: true }).catch(() => ({ data: null })),
        axios.get(`${API}/stripe/connect/transactions`, { withCredentials: true }).catch(() => ({ data: { transactions: [] } }))
      ]);
      
      setConnectStatus(statusRes.data);
      if (earningsRes.data) {
        setEarnings(earningsRes.data);
      }
      if (transactionsRes.data?.transactions) {
        setTransactions(transactionsRes.data.transactions);
      }
    } catch (error) {
      console.error("Error fetching payment data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleOnboarding = async () => {
    setOnboarding(true);
    try {
      const origin = window.location.origin;
      const response = await axios.post(
        `${API}/stripe/connect/onboard`,
        {
          return_url: `${origin}/dashboard/payments?onboarded=true`,
          refresh_url: `${origin}/dashboard/payments?refresh=true`
        },
        { withCredentials: true }
      );
      
      if (response.data.onboarding_url) {
        window.location.href = response.data.onboarding_url;
      } else if (response.data.status === "complete") {
        toast.success("Your payment account is already set up!");
        fetchData();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start onboarding");
    } finally {
      setOnboarding(false);
    }
  };

  const openStripeDashboard = async () => {
    try {
      const response = await axios.post(
        `${API}/stripe/connect/dashboard-link`,
        {},
        { withCredentials: true }
      );
      window.open(response.data.url, "_blank");
    } catch (error) {
      toast.error("Failed to open Stripe dashboard");
    }
  };

  // Check for return from onboarding
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("onboarded") === "true") {
      toast.success("Payment account setup complete!");
      window.history.replaceState({}, "", "/dashboard/payments");
      fetchData();
    } else if (params.get("refresh") === "true") {
      toast.info("Please complete your account setup");
      window.history.replaceState({}, "", "/dashboard/payments");
    }
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="spinner" />
      </div>
    );
  }

  // Feature gating - show locked state for Starter users
  if (!hasPayments) {
    return (
      <div className="space-y-8">
        <div>
          <h1 className="text-3xl font-serif tracking-tight mb-2">Payments</h1>
          <p className="text-muted-foreground">
            Set up and manage how you receive payments from clients
          </p>
        </div>
        <FeatureGate feature="accept_payments">
          {/* Placeholder content shown in locked state */}
          <Card className="h-[300px]">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building className="w-5 h-5" />
                Payment Account
              </CardTitle>
              <CardDescription>
                Connect your Stripe account to receive payments directly from clients
              </CardDescription>
            </CardHeader>
          </Card>
        </FeatureGate>
      </div>
    );
  }

  const isConnected = connectStatus?.connected && connectStatus?.status === "complete";

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-serif tracking-tight mb-2">Payments</h1>
        <p className="text-muted-foreground">
          Set up and manage how you receive payments from clients
        </p>
      </div>

      {/* Connection Status Card */}
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building className="w-5 h-5" />
            Payment Account
          </CardTitle>
          <CardDescription>
            Connect your Stripe account to receive payments directly from clients
          </CardDescription>
        </CardHeader>
        <CardContent>
          {!connectStatus?.connected ? (
            <div className="space-y-4">
              <div className="flex items-start gap-4 p-4 bg-muted/30 rounded-lg">
                <AlertCircle className="w-5 h-5 text-yellow-500 mt-0.5" />
                <div>
                  <p className="font-medium">Payment account not connected</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Set up your payment account to start receiving payments from clients. 
                    This is required to accept quote payments and invoices.
                  </p>
                </div>
              </div>
              
              <Button 
                onClick={handleOnboarding} 
                disabled={onboarding}
                className="btn-primary"
              >
                {onboarding ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Starting Setup...
                  </>
                ) : (
                  <>
                    <CreditCard className="w-4 h-4 mr-2" />
                    Set Up Payment Account
                  </>
                )}
              </Button>
              
              <p className="text-xs text-muted-foreground">
                You will be redirected to Stripe to complete your account setup. 
                This typically takes 5-10 minutes.
              </p>
            </div>
          ) : connectStatus?.status === "pending" ? (
            <div className="space-y-4">
              <div className="flex items-start gap-4 p-4 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
                <Clock className="w-5 h-5 text-yellow-500 mt-0.5" />
                <div>
                  <p className="font-medium">Account setup incomplete</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Please complete your Stripe account setup to start receiving payments.
                  </p>
                  {connectStatus.requirements?.length > 0 && (
                    <p className="text-sm text-yellow-600 mt-2">
                      Remaining items: {connectStatus.requirements.join(", ")}
                    </p>
                  )}
                </div>
              </div>
              
              <Button onClick={handleOnboarding} className="btn-primary">
                <ArrowRight className="w-4 h-4 mr-2" />
                Continue Setup
              </Button>
            </div>
          ) : connectStatus?.status === "restricted" ? (
            <div className="space-y-4">
              <div className="flex items-start gap-4 p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
                <AlertCircle className="w-5 h-5 text-red-500 mt-0.5" />
                <div>
                  <p className="font-medium">Account restricted</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Your payment account has restrictions. Please update your information to continue receiving payments.
                  </p>
                </div>
              </div>
              
              <Button onClick={handleOnboarding} variant="destructive">
                <AlertCircle className="w-4 h-4 mr-2" />
                Fix Account Issues
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-start gap-4 p-4 bg-green-500/10 border border-green-500/20 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-500 mt-0.5" />
                <div>
                  <p className="font-medium">Payment account connected</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    You are all set to receive payments from clients. Funds will be deposited to your connected bank account.
                  </p>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2 text-sm">
                  <span className={`w-2 h-2 rounded-full ${connectStatus.charges_enabled ? 'bg-green-500' : 'bg-yellow-500'}`} />
                  <span>Charges {connectStatus.charges_enabled ? 'enabled' : 'pending'}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <span className={`w-2 h-2 rounded-full ${connectStatus.payouts_enabled ? 'bg-green-500' : 'bg-yellow-500'}`} />
                  <span>Payouts {connectStatus.payouts_enabled ? 'enabled' : 'pending'}</span>
                </div>
              </div>
              
              <Button onClick={openStripeDashboard} variant="outline">
                <ExternalLink className="w-4 h-4 mr-2" />
                Open Stripe Dashboard
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Earnings Summary - Only show if connected */}
      {isConnected && earnings && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="w-5 h-5" />
                Earnings Summary
              </CardTitle>
              <CardDescription>
                Your earnings for the past {earnings.period}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid sm:grid-cols-4 gap-6">
                <div className="text-center p-4 bg-muted/30 rounded-lg">
                  <p className="text-sm text-muted-foreground mb-1">Transactions</p>
                  <p className="text-2xl font-semibold">{earnings.total_transactions}</p>
                </div>
                <div className="text-center p-4 bg-muted/30 rounded-lg">
                  <p className="text-sm text-muted-foreground mb-1">Gross Earnings</p>
                  <p className="text-2xl font-semibold">${earnings.gross_earnings?.toFixed(2)}</p>
                </div>
                <div className="text-center p-4 bg-muted/30 rounded-lg">
                  <p className="text-sm text-muted-foreground mb-1">Platform Fees ({(earnings.commission_rate * 100).toFixed(0)}%)</p>
                  <p className="text-2xl font-semibold text-muted-foreground">-${earnings.platform_fees?.toFixed(2)}</p>
                </div>
                <div className="text-center p-4 bg-green-500/10 border border-green-500/20 rounded-lg">
                  <p className="text-sm text-green-600 mb-1">Net Earnings</p>
                  <p className="text-2xl font-semibold text-green-600">${earnings.net_earnings?.toFixed(2)}</p>
                </div>
              </div>
              
              <p className="text-xs text-muted-foreground mt-4 text-center">
                Based on your {earnings.commission_rate * 100}% platform fee rate. Upgrade your plan for lower fees!
              </p>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Commission Rates Info */}
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle>Platform Fees by Plan</CardTitle>
          <CardDescription>
            Lower your fees by upgrading to a higher tier plan
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid sm:grid-cols-3 gap-4">
            <div className="p-4 bg-muted/30 rounded-lg text-center">
              <p className="font-medium mb-1">Starter</p>
              <p className="text-2xl font-bold text-primary">10%</p>
              <p className="text-xs text-muted-foreground mt-1">Per transaction</p>
            </div>
            <div className="p-4 bg-muted/30 rounded-lg text-center">
              <p className="font-medium mb-1">Pro</p>
              <p className="text-2xl font-bold text-primary">6%</p>
              <p className="text-xs text-muted-foreground mt-1">Per transaction</p>
            </div>
            <div className="p-4 bg-muted/30 rounded-lg text-center">
              <p className="font-medium mb-1">Studio</p>
              <p className="text-2xl font-bold text-primary">3%</p>
              <p className="text-xs text-muted-foreground mt-1">Per transaction</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Transaction History - Only show if connected */}
      {isConnected && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <History className="w-5 h-5" />
                Recent Transactions
              </CardTitle>
              <CardDescription>
                Your payment transaction history
              </CardDescription>
            </CardHeader>
            <CardContent>
              {transactions.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <DollarSign className="w-10 h-10 mx-auto mb-2 opacity-30" />
                  <p>No transactions yet</p>
                  <p className="text-sm">Payments from clients will appear here</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {transactions.slice(0, 10).map((txn, index) => (
                    <motion.div
                      key={txn.transaction_id || index}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: index * 0.05 }}
                      className="flex items-center justify-between p-3 bg-muted/20 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          txn.status === "completed" 
                            ? "bg-green-500/20 text-green-500" 
                            : txn.status === "pending"
                            ? "bg-yellow-500/20 text-yellow-500"
                            : "bg-red-500/20 text-red-500"
                        }`}>
                          {txn.status === "completed" ? (
                            <ArrowDownRight className="w-5 h-5" />
                          ) : txn.status === "pending" ? (
                            <Clock className="w-5 h-5" />
                          ) : (
                            <AlertCircle className="w-5 h-5" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium">
                            {txn.booking_info?.customer_name || txn.payment_type || "Payment"}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {txn.booking_info?.event_type || txn.payment_type === "deposit" ? "Deposit" : "Full Payment"}
                            {txn.created_at && ` • ${new Date(txn.created_at).toLocaleDateString()}`}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-green-500">+${txn.amount?.toFixed(2)}</p>
                        {txn.application_fee > 0 && (
                          <p className="text-xs text-muted-foreground">
                            Fee: -${txn.application_fee?.toFixed(2)}
                          </p>
                        )}
                        <Badge 
                          variant="outline" 
                          className={`text-xs ${
                            txn.status === "completed" 
                              ? "border-green-500/30 text-green-500" 
                              : txn.status === "pending"
                              ? "border-yellow-500/30 text-yellow-500"
                              : "border-red-500/30 text-red-500"
                          }`}
                        >
                          {txn.status}
                        </Badge>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      )}
    </div>
  );
};

export default PaymentsPage;
